import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';
import api from '../api/api';
import { logAnalyticsEvent } from '../common/analytics';
import { captureGpsException, gpsBreadcrumb } from '../common/sentry';
import { log } from '../tools/LogHelper';
import locationSync, {
  GpsPermissionLevel,
  HomeGeofenceConfig,
  LocationCaptureConfig
} from '../tools/location-sync';
import { useUserContext } from './UserContext';

export interface LocationContextValue {
  /** Server-evaluated gate: org flag + eligible enrollment. */
  enabled: boolean | undefined;
  permissionLevel: GpsPermissionLevel;
  refreshConfig: () => Promise<void>;
  requestPermission: () => Promise<GpsPermissionLevel>;
}

const LocationContext = createContext<LocationContextValue>({
  enabled: undefined,
  permissionLevel: 'unknown',
  refreshConfig: async () => undefined,
  requestPermission: async () => 'unknown'
});

/**
 * Server-driven background location capture (CD-255). The device asks the API
 * whether capture applies (GET geolocation/me/config) on login and on every
 * app foreground; enabled:false stops the plugin — that plus the per-upload
 * gate in LocationSync is the fleet kill switch (R13). No location permission
 * is requested and no capture code runs unless the server says enabled.
 */
export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { user } = useUserContext();
  const [enabled, setEnabled] = useState<boolean | undefined>(undefined);
  const [permissionLevel, setPermissionLevel] = useState<GpsPermissionLevel>(
    'unknown'
  );
  const refreshingRef = useRef(false);

  const refreshConfig = useCallback(async () => {
    // background capture is native-only; on web locationSync is a no-op stub,
    // so skip the gate fetch entirely rather than spend a request per foreground
    if (Platform.OS === 'web') return;
    if (!user?.id || refreshingRef.current) return;
    refreshingRef.current = true;
    try {
      const { response } = api.get('geolocation/me/config', {});
      const result = await response;
      if (!result.ok) {
        // Silent before. `enabled` stays undefined and permissionLevel stays
        // 'unknown', so LocationPermissionGate renders nothing — a newly
        // enrolled participant who has not yet granted Always never sees the
        // priming prompt and never starts contributing, and undefined is
        // indistinguishable from "the server says capture is off". A 503 from
        // the capture gate lands here, so it must not be invisible.
        gpsBreadcrumb('config fetch not ok', { status: result.status });
        if (result.status >= 500) {
          captureGpsException(
            new Error(`GPS config fetch failed (${result.status})`),
            { failure: 'configFetch', status: String(result.status) }
          );
        }
        return;
      }
      const config: {
        enabled: boolean;
        captureConfig?: LocationCaptureConfig;
        homeGeofence?: HomeGeofenceConfig | null;
      } = await result.json();
      if (config.enabled && config.captureConfig) {
        await locationSync.configure(
          config.captureConfig,
          {
            onDisabled: () => setEnabled(false),
            onPermissionChange: setPermissionLevel
          },
          // Re-sent on every refresh so a corrected address re-registers the
          // native region without waiting for a reinstall.
          config.homeGeofence
        );
        // Resolve the real permission level BEFORE flipping `enabled`. The
        // gate shows when enabled && level !== 'always', and level starts as
        // 'unknown' — committing enabled first opens a window where a
        // participant who already granted Always sees the priming modal flash
        // on cold start.
        const level = await locationSync.getPermissionLevel();
        setPermissionLevel(level);
        // `enabled` reflects the SERVER's answer and must be committed whether
        // or not tracking can currently run. Starting first and committing
        // after meant a start() rejection (no permission yet) left `enabled`
        // unset, so the priming screen never rendered and permission could
        // never be requested — capture could not start on a fresh install.
        setEnabled(true);
        await locationSync.ensureTracking(level);
      } else {
        setEnabled(config.enabled);
        await locationSync.stop();
      }
    } catch (error) {
      // offline or transient failure: keep the last known capture state
      log('LocationContext: config refresh failed', error);
    } finally {
      refreshingRef.current = false;
    }
  }, [user?.id]);

  const requestPermission = useCallback(async () => {
    logAnalyticsEvent('gps_permission_prompted', {});
    const level = await locationSync.requestPermission();
    setPermissionLevel(level);
    logAnalyticsEvent(
      level === 'always' || level === 'whenInUse'
        ? 'gps_permission_granted'
        : 'gps_permission_denied',
      { level }
    );
    await locationSync.checkin(level);
    // Start immediately on grant rather than waiting for the next foreground or
    // background-fetch tick, so capture begins while the participant is still
    // in the priming flow and can see it working.
    await locationSync.ensureTracking(level);
    return level;
  }, []);

  useEffect(() => {
    if (user?.id) {
      refreshConfig();
    } else {
      locationSync.stop().catch(() => undefined);
      setEnabled(undefined);
    }
  }, [user?.id, refreshConfig]);

  useEffect(() => {
    const previousState = { current: AppState.currentState };
    const subscription = AppState.addEventListener(
      'change',
      (nextState: AppStateStatus) => {
        if (
          previousState.current.match(/inactive|background/) &&
          nextState === 'active'
        ) {
          refreshConfig();
        }
        previousState.current = nextState;
      }
    );
    return () => subscription.remove();
  }, [refreshConfig]);

  return (
    <LocationContext.Provider
      value={{ enabled, permissionLevel, refreshConfig, requestPermission }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useLocationContext = () => useContext(LocationContext);
